import { View } from 'react-native'
import { useState, useEffect, type ComponentProps, useCallback } from 'react'
import {
  type AddressError,
  type Address,
  mapErrors,
  emptyAddress,
  type AutoCompleteSearchMode,
  zipCodeSchema,
  cardApplicationAddressSchema,
  cardPhysicalShipAddressSchema,
} from './AutoCompleteAddressCommon'
import { AutoCompleteInput } from '@/src/common/components/AutoCompleteAddress/AutoCompleteInput'
import FullAddressInputs, { type FullAddressInputsProps } from './FullAddressInput'
import ZipCodeAddressInputs from './ZipCodeAddressInput'
import type { SardineId } from '@/src/services/Sardine/SardineId'

// HACK: I don't like accepting in `errors` from outside the component, but
// we currently don't run validation until submission. Keep this behavior for
// now...?
export interface AutoCompleteAddressProps extends Pick<ComponentProps<typeof View>, 'style'> {
  onChange: (address: Address | undefined, isValid: boolean) => void
  showErrors: boolean
  mode: AutoCompleteSearchMode
  address?: Address
  testIDPrefix?: string
  sardineIds?: { autoComplete?: SardineId; fullAddress?: FullAddressInputsProps['sardineIds'] }
}

export function AutoCompleteAddress(props: AutoCompleteAddressProps) {
  const [address, setAddress] = useState<Address | undefined>(props.address)
  const [validationErrors, setValidationErrors] = useState<AddressError>()
  const validationSchema = (() => {
    switch (props.mode) {
      case 'zip-code':
        return zipCodeSchema
      case 'full-address-unvalidated':
        return undefined
      case 'card-application-address':
        return cardApplicationAddressSchema
      case 'card-physical-ship-address':
        return cardPhysicalShipAddressSchema
    }
  })()

  const AddressInputs = props.mode === 'zip-code' ? ZipCodeAddressInputs : FullAddressInputs
  const { onChange } = props

  const handleAddressChange = useCallback(
    (address: Address | undefined) => {
      setAddress(address)

      if (validationSchema) {
        const validationResult = validationSchema.validate(address, { abortEarly: true })
        const isValid = validationResult.error === undefined

        if (isValid) {
          setValidationErrors(undefined)
        } else {
          const errors = mapErrors(validationResult.error)
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          setValidationErrors(errors as unknown as AddressError)
        }
        onChange(address, isValid)
      } else {
        onChange(address, true)
      }
    },
    [onChange, validationSchema],
  )

  const handleAddressUnlisted = useCallback(() => {
    setAddress(emptyAddress())
  }, [])

  useEffect(() => {
    setAddress(props.address) // Update autocomplete input when address changes externally
  }, [props.address])

  const showAutoCompleteInput = typeof address === 'undefined'

  return (
    <View style={[{ flex: 1 }, props.style]}>
      {showAutoCompleteInput ? (
        <AutoCompleteInput
          mode={props.mode}
          onChange={handleAddressChange}
          onAddressUnlisted={handleAddressUnlisted}
          testIDPrefix={props.testIDPrefix}
          sardineId={props.sardineIds?.autoComplete}
        />
      ) : (
        <AddressInputs
          address={address}
          onChange={handleAddressChange}
          validationErrors={validationErrors}
          showValidationErrors={props.showErrors}
          testIDPrefix={props.testIDPrefix}
          sardineIds={
            AddressInputs === FullAddressInputs ? props.sardineIds?.fullAddress : undefined
          }
        />
      )}
    </View>
  )
}
